import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { IChatContact } from "@/types/(my-space)/chat.interface";
import { ContactList } from "./ContactList";
import { MemberSelector } from "./MemberSelector";
import { MAX_CHAT_MEMBERS, emptySelection, type PeopleSelection } from "../_lib/audience-selection";

const NO_IDS: number[] = [];

interface PeoplePickerDialogProps {
  open: boolean;
  title: string;
  /** single: picking a person confirms at once. */
  mode: "single" | "multiple";
  excludeIds?: number[];
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (people: IChatContact[]) => void;
}

export const PeoplePickerDialog = ({
  open,
  title,
  mode,
  excludeIds,
  isSubmitting,
  onClose,
  onConfirm,
}: PeoplePickerDialogProps) => {
  const { t } = useTranslation("chat");
  const [selection, setSelection] = useState<PeopleSelection>(emptySelection);
  const picked = selection.people;
  const tooMany = picked.length > MAX_CHAT_MEMBERS;

  useEffect(() => {
    if (open) setSelection(emptySelection());
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex h-[min(640px,85vh)] flex-col gap-3 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {open && mode === "multiple" ? (
          <MemberSelector
            className="flex-1"
            selection={selection}
            setSelection={setSelection}
            excludeIds={excludeIds ?? NO_IDS}
            error={
              tooMany ? (
                <p className="text-xs text-danger">
                  {t("new.members_max", { max: MAX_CHAT_MEMBERS })}
                </p>
              ) : null
            }
          />
        ) : open ? (
          <ContactList
            className="flex-1"
            excludeIds={excludeIds}
            onPick={(c) => {
              onConfirm([c]);
              onClose();
            }}
          />
        ) : null}
        {mode === "multiple" ? (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {t("composer.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!picked.length || tooMany}
              isLoading={isSubmitting}
              onClick={() => onConfirm(picked)}
            >
              {t("info.add_count", { count: picked.length })}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
